<template>
  <div :class="['sidebar', { collapsed: collapsed, resizing: isResizing }]"
       :style="sidebarStyle">

    <!-- Resize drag handle (always visible on right edge) -->
    <div class="resize-handle" @mousedown="startResize"></div>

    <!-- Collapsed icon rail -->
    <div v-if="collapsed" class="collapsed-rail">
      <button class="rail-btn" @click="toggleSidebar" title="Expand sidebar" aria-label="Expand sidebar">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
      </button>
      <button class="rail-btn" @click="startNewChat" title="New chat" aria-label="New chat">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
      </button>
      <button class="rail-btn" @click="openSidebar(); $nextTick(() => toggleSearchBar());" title="Search chats" aria-label="Search chats">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </button>
      <button class="rail-btn" @click="openSidebar(); projectsExpanded = true;" title="Projects" aria-label="Projects">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
      </button>
    </div>

    <!-- Expanded sidebar content -->
    <div v-show="!collapsed" class="sidebar-inner">

      <!-- Sidebar header: branding + collapse toggle -->
      <div class="sidebar-header">
        <a class="sidebar-brand" href="#" @click.prevent="startNewChat" title="New chat">
          <span class="brand-name">{{ companyInformation && companyInformation.product_name ? companyInformation.product_name : 'CamGPT' }}</span>
        </a>
        <button class="collapse-btn" @click="toggleSidebar" title="Collapse sidebar">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></svg>
        </button>
      </div>

      <div class="top-section">

        <div class="new-chat-container">
          <button class="new-chat" @click="startNewChat">
            <div class="new-chat-content">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              <span>New chat</span>
            </div>
          </button>
        </div>

        <div class="search-container" :class="{ 'search-active': showSearchBar }">
          <svg class="search-field-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            ref="searchInput"
            v-model="searchQuery"
            class="search-field"
            placeholder="Search chats..."
            @focus="showSearchBar = true"
            @blur="onSearchBlur"
            @keyup.escape="closeSearch"
          />
          <button v-if="searchQuery" class="search-clear-btn" @mousedown.prevent="clearSearch">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <!-- Projects Section -->
        <div class="projects-section" v-if="!collapsed">
          <div class="section-header" @click="toggleProjectsExpanded">
            <span class="section-label">Projects</span>
            <span class="section-toggle">{{ projectsExpanded ? '▾' : '▸' }}</span>
          </div>
          <div v-if="projectsExpanded" class="projects-list">
            <div v-for="project in projects" :key="project.id">
              <div
                class="project-item"
                :class="{ 'selected': selectedProjectId === project.id }"
                @click="selectProject(project)"
              >
                <svg class="project-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                </svg>
                <div class="project-title">{{ project.name }}</div>
                <span v-if="project.is_pinned" class="pin-indicator" title="Pinned">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14l-1.68-2.24a2 2 0 0 1-.4-1.2V6a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v7.56a2 2 0 0 1-.4 1.2z"/></svg>
                </span>
                <button
                  class="project-action-btn"
                  @click.stop="toggleProjectMenu(project.id)"
                  title="Project actions"
                ><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg></button>
                <!-- Project context menu -->
                <div
                  v-if="projectMenuId === project.id"
                  class="project-context-menu"
                  @click.stop
                >
                  <button class="context-menu-item" @click="openEditProject(project)">
                    <svg class="context-menu-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    Edit
                  </button>
                  <button class="context-menu-item" @click="togglePin(project)">
                    <svg class="context-menu-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24z"/></svg>
                    {{ project.is_pinned ? 'Unpin' : 'Pin' }}
                  </button>
                  <button class="context-menu-item" @click="toggleArchive(project)">
                    <svg class="context-menu-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
                    Archive
                  </button>
                  <button class="context-menu-item context-menu-item-danger" @click="confirmDeleteProject(project)">
                    <svg class="context-menu-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
            <button class="new-project-btn" @click="showNewProjectModal = true">
              <svg class="icon-sm" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              <span>New Project</span>
            </button>
            <button v-if="archivedProjects.length > 0" class="archived-toggle-btn" @click="showArchivedProjects = !showArchivedProjects">
              <svg class="context-menu-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
              {{ showArchivedProjects ? 'Hide' : 'Show' }} Archived ({{ archivedProjects.length }})
            </button>
            <template v-if="showArchivedProjects">
              <div v-for="project in archivedProjects" :key="'archived-' + project.id" class="project-item archived-project-item"
                @click="selectProject(project)"
                :class="{ 'selected': selectedProjectId === project.id }"
              >
                <svg class="project-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                </svg>
                <div class="project-title">{{ project.name }}</div>
                <button class="project-action-btn" @click.stop="unarchiveProject(project)" title="Unarchive">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
                </button>
              </div>
            </template>
          </div>
        </div>

        <!-- Conversations Section -->
        <div class="conversations-section">
          <div class="section-header" v-if="!collapsed">
            <span class="section-label">Chats</span>
          </div>
          <div class="conversations">
            <div v-for="conversation in filteredConversations" :key="conversation.id" class="conversation-item"
              @click="loadConversation(conversation)"
              :class="{ 'selected': conversation.id === selectedConversationId }">
              <svg class="chat-item-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>

              <div class="conversation-title">

                <template v-if="editingId === conversation.id">
                  <input
                    v-model="tempName"
                    v-autofocus
                    :ref="`rename_${conversation.id}`"
                    @keyup.enter="renameConversation(conversation)"
                    @blur="cancelEdit()"
                    class="rename-input"
                    :placeholder="conversation.name"
                    autofocus
                  />
                </template>
                <template v-else>{{ conversation.name }}</template>
              </div>

              <button
                v-if="editingId !== conversation.id"
                class="chat-kebab-btn"
                :ref="'chatKebab_' + conversation.id"
                @click.stop="toggleChatMenu(conversation.id, $event)"
                title="Chat options"
              ><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg></button>
              <!-- Chat context menu — fixed position so it escapes overflow clipping -->
              <div
                v-if="chatMenuId === conversation.id"
                class="chat-context-menu"
                :style="chatMenuStyle"
                @click.stop
              >
                <button class="context-menu-item" @click="startEditFromMenu(conversation)">
                  <svg class="context-menu-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Rename
                </button>
                <button v-if="projects.length > 0" class="context-menu-item" @click="openMoveToProjectFromMenu(conversation)">
                  <svg class="context-menu-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                  Move to Project
                </button>
                <button class="context-menu-item context-menu-item-danger" @click="deleteChat(conversation)">
                  <svg class="context-menu-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  Delete Chat
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>

    <!-- Bottom section (inside sidebar-inner so it hides when collapsed) -->
    <div class="bottom-section">
      <div class="menu-item" @click="selectedConversationId = null; $emit('show-memories')">
        <span class="memory-icon icon"></span>
        <span>Memories</span>
      </div>

      <div class="menu-item" @click="toggleTheme">
        <span class="theme-icon icon"></span>
        <span>{{ currentTheme.includes('dark')  ? 'Light Theme' : 'Dark Theme' }}</span>
      </div>

      <a v-if="companyInformation && companyInformation.feedback_url" class="menu-item feedback-link" :href="companyInformation.feedback_url" target="_blank" rel="noopener noreferrer">
        <span class="feedback-icon icon"></span>
        <span>Feedback</span>
      </a>

      <div class="menu-item" @click="$emit('logout')">
        <img
          :src="require('@/assets/icons/logout-icon.svg')"
          alt="Log out Icon" class="icon"/>
        <span>Log Out</span>
      </div>
    </div>

    </div>

    <!-- New Project Modal -->
    <div v-if="showNewProjectModal" class="modal-overlay" @click.self="closeNewProjectModal">
      <div class="modal-content">
        <h3 class="modal-title">Create New Project</h3>
        <div class="form-group">
          <label for="project-name">Project Name</label>
          <input
            id="project-name"
            v-model="newProjectName"
            type="text"
            class="form-input"
            placeholder="Enter project name"
            @keyup.enter="createProject"
            ref="projectNameInput"
          />
        </div>
        <div class="form-group">
          <label for="project-description">Description (optional)</label>
          <textarea
            id="project-description"
            v-model="newProjectDescription"
            class="form-input form-textarea"
            placeholder="Enter project description"
            rows="3"
          ></textarea>
        </div>
        <div v-if="newProjectError" class="form-error">{{ newProjectError }}</div>
        <div class="modal-actions">
          <button class="btn btn-cancel" @click="closeNewProjectModal">Cancel</button>
          <button class="btn btn-create" @click="createProject" :disabled="!newProjectName.trim() || creatingProject">
            {{ creatingProject ? 'Creating...' : 'Create Project' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Move to Project Modal -->
    <div v-if="showMoveToProjectModal" class="modal-overlay" @click.self="closeMoveToProject">
      <div class="modal-content">
        <h3 class="modal-title">Move Chat to Project</h3>
        <p class="move-chat-name">{{ moveTargetConversation?.name }}</p>
        <div class="project-select-list">
          <div
            v-for="proj in projects"
            :key="'move-' + proj.id"
            class="project-select-item"
            :class="{ 'current-project': moveTargetConversation?.project === proj.id }"
            @click="moveConversationToProject(proj.id)"
          >
            <img :src="require('@/assets/icons/project-icon.svg')" alt="Project" class="icon project-icon" />
            <span class="project-select-name">{{ proj.name }}</span>
            <span v-if="moveTargetConversation?.project === proj.id" class="current-badge">Current</span>
          </div>
          <div v-if="projects.length === 0" class="project-select-empty">
            No projects available. Create a project first.
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn btn-cancel" @click="closeMoveToProject">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Edit Project Modal -->
    <div v-if="showEditProjectModal" class="modal-overlay" @click.self="closeEditProjectModal">
      <div class="modal-content">
        <h3 class="modal-title">Edit Project</h3>
        <div class="form-group">
          <label for="edit-project-name">Project Name</label>
          <input
            id="edit-project-name"
            v-model="editProjectName"
            type="text"
            class="form-input"
            placeholder="Enter project name"
            @keyup.enter="saveEditProject"
            ref="editProjectNameInput"
          />
        </div>
        <div class="form-group">
          <label for="edit-project-description">Description (optional)</label>
          <textarea
            id="edit-project-description"
            v-model="editProjectDescription"
            class="form-input form-textarea"
            placeholder="Enter project description"
            rows="3"
          ></textarea>
        </div>
        <div v-if="editProjectError" class="form-error">{{ editProjectError }}</div>
        <div class="modal-actions">
          <button class="btn btn-cancel" @click="closeEditProjectModal">Cancel</button>
          <button class="btn btn-create" @click="saveEditProject" :disabled="!editProjectName.trim() || savingProject">
            {{ savingProject ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Project Confirmation Modal -->
    <div v-if="showDeleteProjectModal" class="modal-overlay" @click.self="closeDeleteProjectModal">
      <div class="modal-content">
        <h3 class="modal-title">Delete Project</h3>
        <p class="delete-warning">
          Are you sure you want to delete <strong>{{ deleteTargetProject?.name }}</strong>?
        </p>
        <p class="delete-details">
          This will permanently delete the project and its documents. Chats in this project will become standalone conversations.
        </p>
        <div class="modal-actions">
          <button class="btn btn-cancel" @click="closeDeleteProjectModal">Cancel</button>
          <button class="btn btn-danger" @click="deleteProject" :disabled="deletingProject">
            {{ deletingProject ? 'Deleting...' : 'Delete Project' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/axios-config';
import { getCurrentTheme, toggleTheme as toggleThemeUtil } from '@/utils/themeManager';

export default {
  directives: {
    autofocus: {
      inserted(el) { el.focus(); el.select && el.select(); },
      mounted(el) { el.focus(); el.select && el.select(); },
    }
  },
  name: 'AppSidebar',
  props: { conversations: Array, conversationMessagesMap: Object, companyInformation: Object, currentUser: Object },
  data() {
    return {
      selectedConversationId: null,
      collapsed: false,
      showSearchBar: false,
      searchQuery: '',
      editingId: null,
      tempName: '',
      screenWidth: window.innerWidth,
      currentTheme: getCurrentTheme(),
      // Resize state
      sidebarWidth: parseInt(localStorage.getItem('sidebarWidth')) || 260,
      isResizing: false,
      preCollapseWidth: parseInt(localStorage.getItem('sidebarWidth')) || 260,
      // Projects
      projects: [],
      selectedProjectId: null,
      projectsExpanded: true,
      showNewProjectModal: false,
      newProjectName: '',
      newProjectDescription: '',
      newProjectError: '',
      creatingProject: false,
      // Project detail
      projectDocuments: [],
      projectConversations: [],
      uploadingDocument: false,
      // Project actions
      projectMenuId: null,
      archivedProjects: [],
      showArchivedProjects: false,
      // Chat context menu
      chatMenuId: null,
      chatMenuStyle: {},
      // Move to project
      showMoveToProjectModal: false,
      moveTargetConversation: null,
      // Edit project
      showEditProjectModal: false,
      editProjectId: null,
      editProjectName: '',
      editProjectDescription: '',
      editProjectError: '',
      savingProject: false,
      // Delete project
      showDeleteProjectModal: false,
      deleteTargetProject: null,
      deletingProject: false,
    };
  },
  computed: {
    sidebarStyle() {
      if (this.collapsed) return { width: null }; // let CSS handle collapsed width
      return { width: this.sidebarWidth + 'px' };
    },
    maxSidebarWidth() {
      return Math.floor(this.screenWidth * 0.25);
    },
    filteredConversations() {
      // First, filter out conversations that belong to projects
      const nonProjectConversations = this.conversations.filter(convo => !convo.project);

      // Then apply search filter if there's a query
      if (!this.searchQuery) return nonProjectConversations;

      const query = this.searchQuery.toLowerCase();

      return nonProjectConversations.filter((convo) => {
        const titleMatch = convo.name?.toLowerCase().includes(query);

        const messages = this.conversationMessagesMap?.[convo.resource_id] || [];

        const historyMatch = messages.some((msg) => {
          const content = msg?.html_content ?? msg?.content ?? '';
          return content.toLowerCase().includes(query);
        });

        return titleMatch || historyMatch;
      });
    }
  },
  methods: {
    startEdit(convo) {
      this.editingId = convo.id;
      this.tempName = convo.name;
      this.$nextTick(() => {
        const el = this.$refs[`rename_${convo.id}`];
        if (el) el.select();
      });
    },
    cancelEdit() {
      this.editingId = null;
      this.tempName = '';
    },

    async renameConversation(convo) {
      const newName = (this.tempName || '').trim();
      if (!newName || newName === convo.name) {
        this.cancelEdit();
        return;
      }

      await api.patch(`/api/conversation/${convo.id}/rename/`, { name: newName });
      this.$emit('refresh-conversations');

      this.cancelEdit();
    },

    // --- Project Methods ---
    async loadProjects() {
      try {
        const response = await api.get('/api/projects/');
        this.projects = response.data;
      } catch (err) {
        console.error('Failed to load projects:', err);
      }
    },
    toggleProjectsExpanded() {
      this.projectsExpanded = !this.projectsExpanded;
    },
    selectProject(project) {
      if (this.selectedProjectId === project.id) {
        // Deselect if clicking same project
        this.selectedProjectId = null;
        this.projectDocuments = [];
        this.projectConversations = [];
        return;
      }
      this.selectedConversationId = null;
      this.selectedProjectId = project.id;
      this.loadProjectDetail(project.id);
      // Emit to show project view in main content area
      this.$emit('show-project-view', project);
    },
    async loadProjectDetail(projectId) {
      try {
        const response = await api.get(`/api/projects/${projectId}/`);
        this.projectDocuments = response.data.documents || [];
        // Load conversations that belong to this project
        this.projectConversations = (this.conversations || []).filter(
          c => c.project === projectId
        );
      } catch (err) {
        console.error('Failed to load project detail:', err);
        this.projectDocuments = [];
        this.projectConversations = [];
      }
    },
    startProjectChat(project) {
      this.$emit('new-chat-in-project', project);
    },
    async handleDocumentUpload(event, projectId) {
      const file = event.target.files[0];
      if (!file) return;
      this.uploadingDocument = true;
      try {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/api/projects/${projectId}/documents/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        await this.loadProjectDetail(projectId);
        // Update the project document count in the list
        const proj = this.projects.find(p => p.id === projectId);
        if (proj) proj.document_count = this.projectDocuments.length;
      } catch (err) {
        console.error('Failed to upload document:', err);
        alert('Failed to upload document. Please try again.');
      } finally {
        this.uploadingDocument = false;
        // Reset the file input
        event.target.value = '';
      }
    },
    async deleteDocument(projectId, docId) {
      try {
        await api.delete(`/api/projects/${projectId}/documents/${docId}/`);
        await this.loadProjectDetail(projectId);
        const proj = this.projects.find(p => p.id === projectId);
        if (proj) proj.document_count = this.projectDocuments.length;
      } catch (err) {
        console.error('Failed to delete document:', err);
      }
    },
    async createProject() {
      const name = this.newProjectName.trim();
      if (!name) return;
      this.creatingProject = true;
      this.newProjectError = '';
      try {
        const response = await api.post('/api/projects/', {
          name: name,
          description: this.newProjectDescription.trim(),
        });
        this.projects.unshift(response.data);
        this.closeNewProjectModal();
        this.$emit('project-created', response.data);
      } catch (err) {
        console.error('Failed to create project:', err);
        this.newProjectError = err.response?.data?.name?.[0] || err.response?.data?.detail || 'Failed to create project. Please try again.';
      } finally {
        this.creatingProject = false;
      }
    },
    closeNewProjectModal() {
      this.showNewProjectModal = false;
      this.newProjectName = '';
      this.newProjectDescription = '';
      this.newProjectError = '';
    },
    // --- Pin/Archive Methods ---
    toggleProjectMenu(projectId) {
      if (this.projectMenuId === projectId) {
        this.projectMenuId = null;
      } else {
        this.projectMenuId = projectId;
      }
    },
    closeProjectMenu() {
      this.projectMenuId = null;
    },
    async togglePin(project) {
      try {
        const response = await api.post(`/api/projects/${project.id}/pin/`);
        project.is_pinned = response.data.is_pinned;
        this.closeProjectMenu();
        // Re-sort projects: pinned first, then by updated_at
        this.projects.sort((a, b) => {
          if (a.is_pinned !== b.is_pinned) return b.is_pinned ? 1 : -1;
          return new Date(b.updated_at) - new Date(a.updated_at);
        });
      } catch (err) {
        console.error('Failed to toggle pin:', err);
      }
    },
    async toggleArchive(project) {
      try {
        const response = await api.post(`/api/projects/${project.id}/archive/`);
        if (response.data.is_archived) {
          // Move from active to archived
          this.projects = this.projects.filter(p => p.id !== project.id);
          project.is_archived = true;
          this.archivedProjects.push(project);
          if (this.selectedProjectId === project.id) {
            this.selectedProjectId = null;
            this.projectDocuments = [];
            this.projectConversations = [];
          }
        } else {
          // Move from archived to active (shouldn't happen from this button, but handle it)
          project.is_archived = false;
          this.archivedProjects = this.archivedProjects.filter(p => p.id !== project.id);
          this.projects.push(project);
          this.projects.sort((a, b) => {
            if (a.is_pinned !== b.is_pinned) return b.is_pinned ? 1 : -1;
            return new Date(b.updated_at) - new Date(a.updated_at);
          });
        }
        this.closeProjectMenu();
      } catch (err) {
        console.error('Failed to toggle archive:', err);
      }
    },
    async unarchiveProject(project) {
      try {
        const response = await api.post(`/api/projects/${project.id}/archive/`);
        if (!response.data.is_archived) {
          project.is_archived = false;
          this.archivedProjects = this.archivedProjects.filter(p => p.id !== project.id);
          this.projects.push(project);
          this.projects.sort((a, b) => {
            if (a.is_pinned !== b.is_pinned) return b.is_pinned ? 1 : -1;
            return new Date(b.updated_at) - new Date(a.updated_at);
          });
        }
      } catch (err) {
        console.error('Failed to unarchive project:', err);
      }
    },
    // --- Edit Project Methods ---
    openEditProject(project) {
      this.editProjectId = project.id;
      this.editProjectName = project.name;
      this.editProjectDescription = project.description || '';
      this.editProjectError = '';
      this.showEditProjectModal = true;
      this.closeProjectMenu();
      this.$nextTick(() => {
        if (this.$refs.editProjectNameInput) {
          this.$refs.editProjectNameInput.focus();
          this.$refs.editProjectNameInput.select();
        }
      });
    },
    closeEditProjectModal() {
      this.showEditProjectModal = false;
      this.editProjectId = null;
      this.editProjectName = '';
      this.editProjectDescription = '';
      this.editProjectError = '';
    },
    async saveEditProject() {
      const name = this.editProjectName.trim();
      if (!name) return;
      this.savingProject = true;
      this.editProjectError = '';
      try {
        const response = await api.patch(`/api/projects/${this.editProjectId}/`, {
          name: name,
          description: this.editProjectDescription.trim(),
        });
        // Update local project data
        const project = this.projects.find(p => p.id === this.editProjectId);
        if (project) {
          project.name = response.data.name;
          project.description = response.data.description;
        }
        this.closeEditProjectModal();
      } catch (err) {
        console.error('Failed to update project:', err);
        this.editProjectError = err.response?.data?.name?.[0] || err.response?.data?.detail || 'Failed to update project. Please try again.';
      } finally {
        this.savingProject = false;
      }
    },
    // --- Delete Project Methods ---
    confirmDeleteProject(project) {
      this.deleteTargetProject = project;
      this.showDeleteProjectModal = true;
      this.closeProjectMenu();
    },
    closeDeleteProjectModal() {
      this.showDeleteProjectModal = false;
      this.deleteTargetProject = null;
    },
    async deleteProject() {
      if (!this.deleteTargetProject) return;
      this.deletingProject = true;
      try {
        await api.delete(`/api/projects/${this.deleteTargetProject.id}/`);
        // Remove from local list
        this.projects = this.projects.filter(p => p.id !== this.deleteTargetProject.id);
        this.archivedProjects = this.archivedProjects.filter(p => p.id !== this.deleteTargetProject.id);
        // Deselect if this was the selected project
        if (this.selectedProjectId === this.deleteTargetProject.id) {
          this.selectedProjectId = null;
          this.projectDocuments = [];
          this.projectConversations = [];
        }
        // Refresh conversations list since chats become standalone
        this.$emit('refresh-conversations');
        this.closeDeleteProjectModal();
      } catch (err) {
        console.error('Failed to delete project:', err);
        alert('Failed to delete project. Please try again.');
      } finally {
        this.deletingProject = false;
      }
    },
    async loadArchivedProjects() {
      try {
        const response = await api.get('/api/projects/?archived=true');
        this.archivedProjects = response.data;
      } catch (err) {
        console.error('Failed to load archived projects:', err);
      }
    },
    // --- Chat Context Menu Methods ---
    toggleChatMenu(conversationId, event) {
      if (this.chatMenuId === conversationId) {
        this.chatMenuId = null;
        return;
      }
      // Position the fixed-position menu relative to the kebab button.
      // Top-half items open downward; bottom-half items open upward.
      const btn = event?.currentTarget;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const inTopHalf = rect.top < window.innerHeight / 2;
        const style = {
          position: 'fixed',
          right: (window.innerWidth - rect.right) + 'px',
        };
        if (inTopHalf) {
          style.top = (rect.bottom + 4) + 'px';
        } else {
          style.bottom = (window.innerHeight - rect.top + 4) + 'px';
        }
        this.chatMenuStyle = style;
      }
      this.chatMenuId = conversationId;
    },
    closeChatMenu() {
      this.chatMenuId = null;
      this.chatMenuStyle = {};
    },
    startEditFromMenu(conversation) {
      this.closeChatMenu();
      this.startEdit(conversation);
    },
    openMoveToProjectFromMenu(conversation) {
      this.closeChatMenu();
      this.openMoveToProject(conversation);
    },
    deleteChat(conversation) {
      this.closeChatMenu();
      this.$emit('clear-conversation', conversation.id);
      if (this.selectedConversationId === conversation.id) {
        this.selectedConversationId = null;
      }
    },
    // --- Move to Project Methods ---
    openMoveToProject(conversation) {
      this.moveTargetConversation = conversation;
      this.showMoveToProjectModal = true;
    },
    closeMoveToProject() {
      this.showMoveToProjectModal = false;
      this.moveTargetConversation = null;
    },
    async removeConversationFromProject(conversation) {
      try {
        await api.post(`/api/conversations/${conversation.id}/move/`, {
          project_id: null,
        });
        // Update local conversation data
        const previousProjectId = conversation.project;
        conversation.project = null;
        // Refresh project detail if viewing that project
        if (this.selectedProjectId === previousProjectId) {
          await this.loadProjectDetail(previousProjectId);
        }
        // Refresh conversations list
        this.$emit('refresh-conversations');
      } catch (err) {
        console.error('Failed to remove conversation from project:', err);
        alert('Failed to remove conversation from project. Please try again.');
      }
    },
    async moveConversationToProject(projectId) {
      if (!this.moveTargetConversation) return;
      // Don't move if already in this project
      if (this.moveTargetConversation.project === projectId) {
        this.closeMoveToProject();
        return;
      }
      try {
        await api.post(`/api/conversations/${this.moveTargetConversation.id}/move/`, {
          project_id: projectId,
        });
        // Update local conversation data
        this.moveTargetConversation.project = projectId;
        // Refresh project detail if viewing that project
        if (this.selectedProjectId === projectId) {
          await this.loadProjectDetail(projectId);
        }
        // Refresh conversations list
        this.$emit('refresh-conversations');
        this.closeMoveToProject();
      } catch (err) {
        console.error('Failed to move conversation to project:', err);
        alert('Failed to move conversation. Please try again.');
      }
    },
    getFirstMessage(conversation) {
      const messages = this.conversationMessagesMap?.[conversation.resource_id] || [];
      return messages.length ? messages[0].content : '';
    },
    clearSearch() {
      this.searchQuery = '';
    },
    toggleSearchBar() {
      this.showSearchBar = !this.showSearchBar;
      this.searchQuery = '';
      if (this.showSearchBar) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
        });
      }
    },
    onSearchBlur() {
      // Keep open if there's a query, otherwise close
      if (!this.searchQuery) {
        this.showSearchBar = false;
      }
    },
    closeSearch() {
      this.searchQuery = '';
      this.showSearchBar = false;
      if (this.$refs.searchInput) this.$refs.searchInput.blur();
    },
    loadConversation(conversation) {
      this.selectedConversationId = conversation.id;
      if (!conversation.project) {
        this.selectedProjectId = null;
      }
      this.$emit('load-conversation', conversation);
    },
    startNewChat() {
      console.log("Sidebar: New Chat clicked, resetting conversation.");
      this.selectedConversationId = null;
      this.$emit('new-chat');
    },
    toggleTheme() {
      this.currentTheme = toggleThemeUtil();
      this.$emit('update-logo');
    },
    toggleSidebar() {
      this.collapsed = !this.collapsed;
      if (!this.collapsed) {
        // Restore previous width on expand
        this.sidebarWidth = this.preCollapseWidth;
        this.$emit('sidebar-width', this.sidebarWidth);
      }
      this.$emit('collapsed', this.collapsed);
    },
    openSidebar() {
      if (this.collapsed) {
        this.collapsed = false;
        this.sidebarWidth = this.preCollapseWidth;
        this.$emit('sidebar-width', this.sidebarWidth);
        this.$emit('collapsed', false);
      }
    },
    // --- Drag Resize Methods ---
    startResize(e) {
      e.preventDefault();
      this.isResizing = true;
      document.addEventListener('mousemove', this.onResize);
      document.addEventListener('mouseup', this.stopResize);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    onResize(e) {
      const minWidth = 120;
      const collapseThreshold = 100;
      const expandThreshold = 80;
      let newWidth = e.clientX;

      if (this.collapsed) {
        // Dragging right from collapsed state — expand once past threshold
        if (newWidth > expandThreshold) {
          this.collapsed = false;
          this.sidebarWidth = Math.max(minWidth, Math.min(newWidth, this.maxSidebarWidth));
          this.$emit('collapsed', false);
          this.$emit('sidebar-width', this.sidebarWidth);
        }
        return;
      }

      if (newWidth < collapseThreshold) {
        // Snap to collapsed
        this.isResizing = false;
        document.removeEventListener('mousemove', this.onResize);
        document.removeEventListener('mouseup', this.stopResize);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        this.collapsed = true;
        this.$emit('collapsed', true);
        return;
      }

      newWidth = Math.max(minWidth, Math.min(newWidth, this.maxSidebarWidth));
      this.sidebarWidth = newWidth;
      this.$emit('sidebar-width', newWidth);
    },
    stopResize() {
      this.isResizing = false;
      document.removeEventListener('mousemove', this.onResize);
      document.removeEventListener('mouseup', this.stopResize);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Persist width if expanded
      if (!this.collapsed) {
        this.preCollapseWidth = this.sidebarWidth;
        localStorage.setItem('sidebarWidth', this.sidebarWidth);
      }
    },
    handleCollapse(val) {
      this.collapsed = val;
    },

    // Dynamically collapse the sidebar based on the screen width
    handleResize() {
      this.screenWidth = window.innerWidth;

      // Clamp sidebar width if window shrunk past max
      if (!this.collapsed && this.sidebarWidth > this.maxSidebarWidth) {
        this.sidebarWidth = this.maxSidebarWidth;
        this.$emit('sidebar-width', this.sidebarWidth);
      }

      // Collapse the sidebar if the width is less than 768px
      if (this.screenWidth < 768) {
        this.collapsed = true;
      } else if (this.collapsed && this.screenWidth >= 768) {
        this.collapsed = false;
        this.sidebarWidth = this.preCollapseWidth;
        this.$emit('sidebar-width', this.sidebarWidth);
      }

      this.$emit('collapsed', this.collapsed);
    },
  },
  watch: {
    showNewProjectModal(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.projectNameInput) {
            this.$refs.projectNameInput.focus();
          }
        });
      }
    },
    showEditProjectModal(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.editProjectNameInput) {
            this.$refs.editProjectNameInput.focus();
            this.$refs.editProjectNameInput.select();
          }
        });
      }
    },
  },
  mounted() {
    // Add an event listener to watch for window resizing
    window.addEventListener('resize', this.handleResize);

    // Emit initial sidebar width
    this.$emit('sidebar-width', this.sidebarWidth);

    // Initial resize check on component mount
    this.handleResize();

    // Load projects
    this.loadProjects();
    this.loadArchivedProjects();

    // Close context menus when clicking outside
    this._closeMenuHandler = () => {
      if (this.projectMenuId !== null) {
        this.closeProjectMenu();
      }
      if (this.chatMenuId !== null) {
        this.closeChatMenu();
      }
    };
    document.addEventListener('click', this._closeMenuHandler);

    // Dismiss context menus and modals on Escape
    this._escapeHandler = (event) => {
      if (event.key !== 'Escape') return;
      if (this.projectMenuId !== null) { this.closeProjectMenu(); return; }
      if (this.chatMenuId !== null) { this.closeChatMenu(); return; }
      if (this.showNewProjectModal) { this.closeNewProjectModal(); return; }
      if (this.showMoveToProjectModal) { this.closeMoveToProject(); return; }
      if (this.showEditProjectModal) { this.closeEditProjectModal(); return; }
      if (this.showDeleteProjectModal) { this.closeDeleteProjectModal(); return; }
    };
    document.addEventListener('keydown', this._escapeHandler);
  },
  beforeUnmount() {
    // Clean up the event listener
    window.removeEventListener('resize', this.handleResize);
    if (this._closeMenuHandler) {
      document.removeEventListener('click', this._closeMenuHandler);
    }
    if (this._escapeHandler) {
      document.removeEventListener('keydown', this._escapeHandler);
    }
  },
};
</script>

<style scoped>
.sidebar {
  position: relative;
  width: var(--sidebar-width);
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--bg-sidebar);
  border-right: 1px solid var(--border-subtle);
  padding: var(--space-lg);
  box-sizing: border-box;
  font-family: var(--font-sans);
  z-index: 20;
  transition: width var(--duration-slow) var(--ease-default),
              background-color var(--duration-base) var(--ease-default);
  flex-shrink: 0;
}

.sidebar-inner {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* ========== Resize Handle ========== */
.resize-handle {
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 10;
  transition: background-color var(--duration-base) var(--ease-default);
}

.resize-handle:hover,
.sidebar.resizing .resize-handle {
  background-color: var(--accent-primary);
  opacity: 0.5;
}

.sidebar.resizing {
  transition: none;
}

/* ========== Sidebar Header ========== */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-xs) 0;
  margin-bottom: var(--space-sm);
  flex-shrink: 0;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  min-width: 0;
  text-decoration: none;
  cursor: pointer;
}

.sidebar-brand:hover .brand-name {
  opacity: 0.8;
}

.brand-name {
  font-family: var(--font-display);
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: var(--tracking-tight);
  transition: opacity var(--duration-base) var(--ease-default);
}

.collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--text-muted);
  flex-shrink: 0;
  transition: var(--transition-colors);
}

.collapse-btn:hover {
  background: var(--bg-surface-hover);
  color: var(--text-primary);
}

/* ========== Collapsed State ========== */
.sidebar.collapsed {
  width: var(--sidebar-collapsed-width);
  padding: 0;
  border-right: 1px solid var(--border-subtle);
}

/* ========== Collapsed Icon Rail ========== */
.collapsed-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-md) 0;
  gap: var(--space-xs);
  width: 100%;
  height: 100%;
}

.rail-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--text-muted);
  transition: var(--transition-colors);
  flex-shrink: 0;
}

.rail-btn:hover {
  background: var(--bg-surface-hover);
  color: var(--text-primary);
}

.rail-btn:first-child {
  margin-bottom: var(--space-md);
}

.top-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.conversations-section {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  gap: var(--space-sm);
  flex: 1;
  min-height: 0;
}

.conversations-section::-webkit-scrollbar {
  width: 6px;
}

.conversations-section::-webkit-scrollbar-track {
  background: transparent;
}

.conversations-section::-webkit-scrollbar-thumb {
  background-color: var(--scrollbar-thumb);
  border-radius: var(--radius-pill);
}

.conversations-section::-webkit-scrollbar-thumb:hover {
  background-color: var(--scrollbar-thumb-hover);
}


.new-chat-container {
  position: sticky;
  top: 0;
  z-index: 1;
}

.new-chat {
  width: 100%;
  height: 44px;
  border: none;
  padding: var(--space-sm) var(--space-lg);
  background: var(--accent-primary);
  color: var(--text-on-accent);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  font-family: var(--font-sans);
  transition: var(--transition-colors), var(--transition-shadow), var(--transition-transform);
  border-radius: var(--radius-pill);
  cursor: pointer;
  margin-top: var(--space-sm);
  box-shadow: var(--shadow-card);
}

.new-chat:hover {
  background: var(--accent-hover);
  box-shadow: var(--shadow-card-hover);
  transform: translateY(-1px);
}

.new-chat:active {
  transform: translateY(0);
  box-shadow: var(--shadow-sm);
}

.new-chat-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  box-sizing: border-box;
  width: 100%;
}

.new-chat-content svg {
  flex-shrink: 0;
}

/* ========== Inline Search Field ========== */
.search-container {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-md);
  border: 1px solid transparent;
  background: var(--bg-surface-hover);
  transition: var(--transition-colors), var(--transition-shadow);
  cursor: text;
}

.search-container:hover {
  background: var(--bg-surface);
  border-color: var(--border-subtle);
}

.search-container.search-active {
  background: var(--bg-surface);
  border-color: var(--border-focus);
  box-shadow: var(--shadow-input-focus);
}

.search-field-icon {
  flex-shrink: 0;
  color: var(--text-muted);
  transition: color var(--duration-base) var(--ease-default);
}

.search-container.search-active .search-field-icon {
  color: var(--accent-primary);
}

.search-field {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-primary);
  padding: var(--space-xs) 0;
  min-width: 0;
}

.search-field::placeholder {
  color: var(--text-muted);
}

.search-clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: var(--text-muted);
  border-radius: var(--radius-sm);
  transition: var(--transition-colors);
  flex-shrink: 0;
}

.search-clear-btn:hover {
  color: var(--text-primary);
  background: var(--bg-surface-hover);
}

/* Inline SVG chat bubble icon — uses currentColor, no filter hack needed */
.chat-item-icon {
  flex-shrink: 0;
  color: var(--text-muted);
  margin-right: 6px;
  opacity: 0.7;
  transition: color var(--duration-base) var(--ease-default);
}

.conversation-item:hover .chat-item-icon {
  color: var(--text-primary);
  opacity: 1;
}

.conversation-item.selected .chat-item-icon {
  color: var(--accent-primary);
  opacity: 1;
}

.conversations {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.conversation-item {
  position: relative;
  display: flex;
  align-items: center;
  padding: 5px var(--space-sm);
  font-size: 0.8rem;
  color: var(--text-primary);
  cursor: pointer;
  transition: var(--transition-colors);
  border-radius: var(--radius-md);
  border-left: 3px solid transparent;
}

.conversation-item:hover {
  background-color: var(--bg-surface-hover);
}

.conversation-item.selected {
  background-color: var(--accent-muted);
  border-left-color: var(--accent-primary);
  border-radius: var(--radius-md);
}

.conversation-title {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  /* Keep text on single line */
  overflow: hidden;
  /* Hide overflow text */
  text-overflow: ellipsis;
  /* Show ellipsis for truncated text */
  line-height: 1.3;
}

.rename-input {
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-sm);
  line-height: 1.2;
  background-color: var(--bg-surface);
  color: var(--text-primary);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  transition: var(--transition-colors), var(--transition-shadow);
}

.rename-input:focus {
  outline: none;
  border-color: var(--border-focus);
  box-shadow: var(--shadow-input-focus);
}

.bottom-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding-top: var(--space-lg);
  flex-shrink: 0;
  border-top: 1px solid var(--border-subtle);
  background-color: var(--bg-sidebar);
  z-index: 1;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: var(--text-sm);
  color: var(--text-tertiary);
  cursor: pointer;
  padding: var(--space-sm) var(--space-sm);
  border-radius: var(--radius-md);
  transition: var(--transition-colors);
}

.menu-item:hover {
  background-color: var(--bg-surface-hover);
  color: var(--text-primary);
}

.menu-item img {
  width: 20px;
  height: 20px;
  opacity: 0.7;
}

/* Theme icon - styled via CSS based on current theme */
.theme-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  background-image: url('@/assets/icons/light-mode-icon.svg');
}

[data-theme="dark"] .theme-icon {
  background-image: url('@/assets/icons/dark-mode-icon.svg');
}

/* Memory icon - styled via CSS based on current theme */
.memory-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  /* Use a brain/memory emoji as background via SVG data URI for light theme */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z'/%3E%3Cpath d='M9 21h6'/%3E%3Cpath d='M10 17v4'/%3E%3Cpath d='M14 17v4'/%3E%3C/svg%3E");
}

[data-theme="dark"] .memory-icon {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23ccc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z'/%3E%3Cpath d='M9 21h6'/%3E%3Cpath d='M10 17v4'/%3E%3Cpath d='M14 17v4'/%3E%3C/svg%3E");
}

/* Knowledge icon (book) - admin only */
.knowledge-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 19.5A2.5 2.5 0 0 1 6.5 17H20'/%3E%3Cpath d='M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z'/%3E%3C/svg%3E");
}

[data-theme="dark"] .knowledge-icon {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23ccc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 19.5A2.5 2.5 0 0 1 6.5 17H20'/%3E%3Cpath d='M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z'/%3E%3C/svg%3E");
}

/* Feedback icon - styled via CSS based on current theme */
.feedback-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  background-image: url('@/assets/icons/feedback-icon.svg');
}

[data-theme="dark"] .feedback-icon {
  background-image: url('@/assets/icons/feedback-icon-dark.svg');
}

.feedback-link {
  text-decoration: none;
  color: inherit;
}

/* --- Projects Section --- */
.projects-section {
  margin-bottom: 8px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
  cursor: pointer;
  user-select: none;
}

.section-label {
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
  color: var(--text-muted);
}

.section-toggle {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.projects-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
}

.project-item {
  display: flex;
  align-items: center;
  padding: var(--space-xs) var(--space-sm);
  font-size: var(--text-sm);
  color: var(--text-primary);
  cursor: pointer;
  transition: var(--transition-colors);
  border-radius: var(--radius-md);
  gap: var(--space-xs);
  border-left: 3px solid transparent;
}

.project-item:hover {
  background-color: var(--bg-surface-hover);
}

.project-item.selected {
  background-color: var(--accent-muted);
  border-left-color: var(--accent-primary);
}

.project-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--text-tertiary);
  transition: color 0.15s ease;
}

.project-item:hover .project-icon,
.project-item.selected .project-icon {
  color: var(--accent-primary);
}

.project-title {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pin-indicator {
  flex-shrink: 0;
  color: var(--accent-primary);
  display: flex;
  align-items: center;
}

.new-project-btn {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  padding: var(--space-xs) var(--space-sm);
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--text-sm);
  font-family: var(--font-sans);
  color: var(--text-muted);
  border-radius: var(--radius-md);
  transition: var(--transition-colors);
}

.new-project-btn:hover {
  background-color: var(--bg-surface-hover);
  color: var(--text-primary);
}

.icon-sm {
  width: 16px;
  height: 16px;
}

/* --- Modal Styles --- */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn var(--duration-base) var(--ease-default);
}

.modal-content {
  background: var(--bg-surface);
  border-radius: var(--radius-xl);
  padding: var(--space-xl);
  width: 420px;
  max-width: 90vw;
  box-shadow: var(--shadow-float);
  border: 1px solid var(--border-subtle);
  animation: fadeInScale var(--duration-slow) var(--ease-out);
}

.modal-title {
  margin: 0 0 var(--space-xl) 0;
  font-size: var(--text-md);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
}

.form-group {
  margin-bottom: var(--space-lg);
}

.form-group label {
  display: block;
  margin-bottom: var(--space-xs);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--text-tertiary);
}

.form-input {
  width: 100%;
  padding: var(--space-md) var(--space-md);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  font-family: var(--font-sans);
  background: var(--bg-surface);
  color: var(--text-primary);
  box-sizing: border-box;
  transition: var(--transition-colors), var(--transition-shadow);
}

.form-input:focus {
  outline: none;
  border-color: var(--border-focus);
  box-shadow: var(--shadow-input-focus);
}

.form-textarea {
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
}

.form-error {
  color: #dc4a4a;
  font-size: var(--text-sm);
  margin-bottom: var(--space-md);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-sm);
  margin-top: var(--space-sm);
}

.btn {
  padding: var(--space-sm) var(--space-xl);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  font-family: var(--font-sans);
  cursor: pointer;
  border: none;
  transition: var(--transition-colors), var(--transition-shadow);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-cancel {
  background: var(--bg-surface-hover);
  color: var(--text-primary);
}

.btn-cancel:hover {
  background: var(--bg-hover);
}

.btn-create {
  background: var(--accent-primary);
  color: var(--text-on-accent);
}

.btn-create:hover:not(:disabled) {
  background: var(--accent-hover);
}

/* --- Project Detail / Documents --- */
.project-detail {
  padding: 4px 0 8px 26px;
}

.project-description {
  font-size: 0.72rem;
  color: var(--text-secondary, #888);
  font-style: italic;
  margin-bottom: 6px;
  line-height: 1.3;
  word-break: break-word;
}

.project-docs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.project-docs-label {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--text-secondary, #888);
}

.upload-btn {
  font-size: 0.72rem;
  color: var(--text-secondary, #666);
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.upload-btn:hover {
  background-color: var(--hover-bg, #e4e4e4);
  color: var(--text-primary, #1C1C1C);
}

.upload-btn.uploading {
  opacity: 0.5;
  cursor: not-allowed;
}

.file-input-hidden {
  display: none;
}

.project-docs-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.project-doc-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 4px;
  font-size: 0.78rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.project-doc-item:hover {
  background-color: var(--hover-bg, #f0f0f0);
}

.doc-icon {
  font-size: 0.7rem;
  flex-shrink: 0;
}

.doc-name {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text-primary, #1C1C1C);
}

.doc-delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary, #999);
  font-size: 0.9rem;
  padding: 0 2px;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

.project-doc-item:hover .doc-delete-btn {
  opacity: 1;
}

.doc-delete-btn:hover {
  color: #e53e3e;
}

.project-docs-empty {
  font-size: 0.72rem;
  color: var(--text-secondary, #999);
  font-style: italic;
  padding: 2px 0;
}

.project-new-chat-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 5px 6px;
  background: var(--accent-light, #e8f4fd);
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.78rem;
  color: var(--text-primary, #1C1C1C);
  transition: background-color 0.2s;
  margin-bottom: 6px;
}

.project-new-chat-btn:hover {
  background: var(--accent, #8ECAE6);
}

.project-action-buttons {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
}

.project-action-buttons .project-new-chat-btn {
  flex: 1;
  margin-bottom: 0;
}

.project-artifacts-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 8px;
  background: var(--accent-light, #e8f4fd);
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.78rem;
  color: var(--text-primary, #1C1C1C);
  transition: background-color 0.2s;
  white-space: nowrap;
}

.project-artifacts-btn:hover {
  background: var(--accent, #8ECAE6);
}

.project-artifacts-btn svg {
  flex-shrink: 0;
}

.project-chats-section {
  margin-bottom: 6px;
}

.project-chat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 4px;
  font-size: 0.78rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.project-chat-item:hover {
  background-color: var(--hover-bg, #f0f0f0);
}

.chat-remove-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary, #999);
  font-size: 0.9rem;
  padding: 0 2px;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

.project-chat-item:hover .chat-remove-btn {
  opacity: 1;
}

.chat-remove-btn:hover {
  color: #e53e3e;
}

.icon-xs {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

/* --- Project Action Button & Context Menu --- */
.project-action-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  padding: 2px;
  opacity: 0;
  transition: opacity var(--duration-base) var(--ease-default),
              color var(--duration-base) var(--ease-default);
  flex-shrink: 0;
  line-height: 1;
  display: flex;
  align-items: center;
  border-radius: var(--radius-sm);
}

.project-action-btn:hover {
  color: var(--text-primary);
  background: var(--bg-surface-hover);
}

.project-item:hover .project-action-btn {
  opacity: 1;
}

.project-context-menu {
  position: absolute;
  right: 0;
  top: 100%;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-float);
  z-index: 100;
  min-width: 150px;
  padding: var(--space-xs) 0;
  overflow: hidden;
  animation: fadeInScale var(--duration-base) var(--ease-out);
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  width: 100%;
  padding: var(--space-sm) var(--space-md);
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--text-sm);
  font-family: var(--font-sans);
  color: var(--text-primary);
  transition: var(--transition-colors);
  text-align: left;
}

.context-menu-item:hover {
  background-color: var(--bg-surface-hover);
}

.context-menu-icon {
  flex-shrink: 0;
  opacity: 0.7;
}

/* Make project-item relative for context menu positioning */
.project-item {
  position: relative;
}

/* Archived projects toggle button */
.archived-toggle-btn {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  width: 100%;
  padding: var(--space-xs) var(--space-sm);
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--text-xs);
  font-family: var(--font-sans);
  color: var(--text-muted);
  border-radius: var(--radius-md);
  transition: var(--transition-colors);
  margin-top: var(--space-xs);
}

.archived-toggle-btn:hover {
  background-color: var(--bg-surface-hover);
  color: var(--text-primary);
}

/* Archived project items have a muted appearance */
.archived-project-item {
  opacity: 0.65;
}

.archived-project-item:hover {
  opacity: 0.85;
}

.archived-project-item .project-action-btn {
  opacity: 0.7;
}

.archived-project-item:hover .project-action-btn {
  opacity: 1;
}

/* --- Chat Kebab Menu Button --- */
.chat-kebab-btn {
  flex: 0 0 auto;
  opacity: 0;
  pointer-events: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  margin-left: var(--space-xs);
  margin-right: var(--space-xs);
  transition: opacity var(--duration-base) var(--ease-default),
              color var(--duration-base) var(--ease-default);
  line-height: 1;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  border-radius: var(--radius-sm);
}

.conversation-item:hover .chat-kebab-btn,
.conversation-item:focus-within .chat-kebab-btn {
  opacity: 0.6;
  pointer-events: auto;
}

.chat-kebab-btn:hover {
  opacity: 1;
  color: var(--text-primary);
  background: var(--bg-surface-hover);
}

/* --- Chat Context Menu --- */
.chat-context-menu {
  /* position, right, bottom are set dynamically via :style binding */
  position: fixed;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-float);
  z-index: 200;
  min-width: 170px;
  padding: var(--space-xs) 0;
  overflow: hidden;
  animation: fadeInScale var(--duration-base) var(--ease-out);
}

/* --- Move to Project Modal --- */
.move-chat-name {
  font-size: 0.85rem;
  color: var(--text-secondary, #666);
  margin: 0 0 12px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.project-select-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.project-select-item {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-md);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition-colors);
  font-size: var(--text-base);
  color: var(--text-primary);
}

.project-select-item:hover {
  background-color: var(--bg-surface-hover);
}

.project-select-item.current-project {
  background-color: var(--accent-muted);
  cursor: default;
}

.project-select-name {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.current-badge {
  font-size: var(--text-xs);
  color: var(--accent-primary);
  padding: 2px var(--space-sm);
  border-radius: var(--radius-pill);
  background: var(--accent-muted);
  flex-shrink: 0;
  font-weight: var(--font-medium);
}

.project-select-empty {
  font-size: 0.85rem;
  color: var(--text-secondary, #999);
  text-align: center;
  padding: 20px;
  font-style: italic;
}

/* --- Delete Confirmation Modal --- */
.delete-warning {
  font-size: 0.9rem;
  color: var(--text-primary, #1C1C1C);
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.delete-details {
  font-size: 0.82rem;
  color: var(--text-secondary, #666);
  margin: 0 0 16px 0;
  line-height: 1.4;
}

.btn-danger {
  background: #dc4a4a;
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  background: #c53030;
  box-shadow: var(--shadow-card);
}

/* --- Context Menu Danger Item --- */
.context-menu-item-danger {
  color: #dc4a4a;
}

.context-menu-item-danger:hover {
  background-color: rgba(220, 74, 74, 0.08);
}

/* --- Empty Project State --- */
.project-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 8px;
  text-align: center;
}

.project-empty-icon {
  color: var(--text-secondary, #999);
  margin-bottom: 8px;
  opacity: 0.5;
}

.project-empty-text {
  margin: 0 0 4px 0;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--text-primary, #1C1C1C);
}

.project-empty-hint {
  margin: 0 0 12px 0;
  font-size: 0.72rem;
  color: var(--text-secondary, #999);
}

.project-empty-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.project-empty-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 500;
  transition: background-color 0.2s, opacity 0.2s;
}

.project-empty-btn-primary {
  background: var(--accent-primary);
  color: var(--text-on-accent);
}

.project-empty-btn-primary:hover {
  background: var(--accent-hover);
}

.project-empty-btn-secondary {
  background: var(--hover-bg, #f0f0f0);
  color: var(--text-primary, #1C1C1C);
}

.project-empty-btn-secondary:hover {
  background: var(--accent-light, #e8f4fd);
}

.project-empty-btn-secondary.uploading {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>